import {
  createContext,
  useState,
  useMemo,
  useCallback,
  PropsWithChildren
} from 'react'
import { Snackbar, Alert, AlertTitle, Box } from '@mui/material'

type AlertMode = 'replace' | 'stack'

type AlertObject = {
  type: 'success' | 'info' | 'warning' | 'error'
  title?: string
  message: string
  autoHide?: boolean
  mode?: AlertMode
}

type SnackbarItem = Required<Pick<AlertObject, 'type' | 'message'>> &
  Omit<AlertObject, 'type' | 'message'> & {
    id: string
  }

const MAX_STACK = 5

export const SnackbarContext = createContext<{
  showAlertSnackbar: (params: AlertObject) => void
}>({
  showAlertSnackbar: () => {}
})

export function SnackbarProvider(props: PropsWithChildren) {
  const [snackbars, setSnackbars] = useState<SnackbarItem[]>([])

  const handleClose = useCallback((id: string) => {
    setSnackbars(prev => prev.filter(item => item.id !== id))
  }, [])

  const showAlertSnackbar = useCallback((alertObject: AlertObject) => {
    const newItem: SnackbarItem = {
      ...alertObject,
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    }

    setSnackbars(prev => {
      // Default mode is 'replace': clear all existing, show only the new one
      if (!alertObject.mode || alertObject.mode === 'replace') {
        return [newItem]
      }

      // Stack mode: append to existing, cap at MAX_STACK
      return [...prev, newItem].slice(-MAX_STACK)
    })
  }, [])

  const value = useMemo(
    () => ({ showAlertSnackbar }),
    [showAlertSnackbar]
  )

  return (
    <SnackbarContext.Provider value={value}>
      {/* Render each snackbar independently so they can auto-dismiss individually */}
      <Box
        sx={{
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: theme => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          pointerEvents: 'none'
        }}>
        {snackbars.map(snackbar => (
          <Snackbar
            key={snackbar.id}
            open={true}
            onClose={() => handleClose(snackbar.id)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={
              snackbar.message && snackbar.autoHide !== false
                ? Math.max(snackbar.message.length * 100, 3000)
                : null
            }
            sx={{
              position: 'relative',
              top: 'auto',
              left: 'auto',
              right: 'auto',
              bottom: 'auto',
              transform: 'none',
              pointerEvents: 'auto'
            }}>
            <Alert
              severity={snackbar.type}
              onClose={() => handleClose(snackbar.id)}
              // The container above is position: fixed, so a long message runs
              // past the viewport bottom where page scroll cannot reach it.
              // Scoped to the message slot: onClose renders into the sibling
              // .MuiAlert-action slot, which stays pinned and reachable.
              sx={{
                '& .MuiAlert-message': {
                  maxHeight: 'calc(100vh - 96px)',
                  overflowY: 'auto'
                }
              }}>
              {snackbar.title && <AlertTitle>{snackbar.title}</AlertTitle>}
              {snackbar.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
      {props.children}
    </SnackbarContext.Provider>
  )
}
